import { filterHdJustNowPersonalityActivations } from '../../mandala/just-now-transit/hd-just-now-transit';
import { deriveCenters } from '../basic-properties/hd-chart-properties.util';
import { HD_BODYGRAPH_GATE_TO_CENTER, HD_BODYGRAPH_GATES_BY_CENTER } from '../hd-bodygraph-metadata';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdBodygraphCenter, HdBodygraphConfig, HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HD_NOT_SELF_BY_IMPACT_ORDER } from '../hd-center-not-self-metadata';
import { buildHdCycleConditionedCenters } from '../hd-cycle-cards.builder';
import { filterHdCycleOverlayActivations } from '../hd-cycle-overlay.utils';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HdPlanetKey,
  getHdPlanetDisplayConfigByPlanetId,
} from '../planet-grid/hd-planet-display-config';

/**
 * Keuzelijsten van het "Mijn mind zegt"-logboek (spec
 * `docs/superpowers/specs/2026-10-22-mind-logboek-design.md` §4): de centra en
 * poorten van de bronchart, en de conditioneerder-suggesties (transit op het
 * tijdstip, of een persoon) die het gekozen centrum raken. Pure functies op de
 * bestaande bouwstenen: `deriveCenters` (openheid), `buildHdCycleConditionedCenters`
 * (welke activaties van de ander een kanaal met de bronchart afmaken).
 */

export interface HdMindCenterOption {
  readonly center: HdBodygraphCenter;
  /** Ongedefinieerd in de bronchart: daar komt conditionering het sterkst binnen. */
  readonly open: boolean;
}

export interface HdMindGateOption {
  readonly gate: number;
  /** Door de bronchart zelf geactiveerd (gedempt in de UI; een hangende poort kan toch geconditioneerd worden). */
  readonly ownActivated: boolean;
}

export interface HdMindGateSuggestion {
  readonly planetKey: HdPlanetKey;
  readonly planetId: number;
  readonly glyph: string;
  readonly color: string;
  readonly gate: number;
  readonly line: number;
  readonly center: HdBodygraphCenter;
  /** De activatie maakt samen met de bronchart een kanaal af (en definieert zo het centrum). */
  readonly completesChannel: boolean;
}

export interface HdMindSuggestionInput {
  readonly ownConfiguration: HdBodygraphConfig;
  readonly otherActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly renderStateFactory: HdBodygraphRenderStateFactory;
  /** Gekozen centrum; null = alle ongedefinieerde centra van de bronchart. */
  readonly center: HdBodygraphCenter | null;
  /** Transit = enkel de personality-kant van het moment (zoals Just Now); persoon = beide kanten. */
  readonly kind: 'transit' | 'person';
}

const PLANET_ORDER: ReadonlyMap<number, number> = new Map(
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map((config, index) => [config.planetId, index]),
);

/**
 * De negen centra in niet-zelf-impactvolgorde, de ongedefinieerde (open)
 * vooraan: daar zit de conditionering die de lecture bedoelt, maar een
 * gedefinieerd centrum blijft kiesbaar (hangende poorten).
 */
export function buildHdMindCenterOptions(
  ownConfiguration: HdBodygraphConfig,
  renderStateFactory: HdBodygraphRenderStateFactory,
): HdMindCenterOption[] {
  const centers = deriveCenters(renderStateFactory.create(ownConfiguration));
  const open = new Set(centers.undefined.map(entry => entry.center));
  const ordered = HD_NOT_SELF_BY_IMPACT_ORDER.map(center => ({ center, open: open.has(center) }));
  return [...ordered.filter(option => option.open), ...ordered.filter(option => !option.open)];
}

/** De poorten van een centrum, oplopend, met de eigen activaties van de bronchart gemarkeerd. */
export function buildHdMindGateOptions(ownConfiguration: HdBodygraphConfig, center: HdBodygraphCenter): HdMindGateOption[] {
  const ownGates = new Set(ownConfiguration.activations.map(activation => activation.gate));
  return (HD_BODYGRAPH_GATES_BY_CENTER[center] ?? []).map(gate => ({ gate, ownActivated: ownGates.has(gate) }));
}

/**
 * Conditioneerder-suggesties: de activaties van de ander (transit of persoon)
 * in het gekozen centrum, of in alle open centra zolang er geen centrum
 * gekozen is. Activaties die een kanaal met de bronchart afmaken staan eerst,
 * daarna in planeetvolgorde; één regel per planeet-poort-lijn.
 */
export function buildHdMindGateSuggestions(input: HdMindSuggestionInput): HdMindGateSuggestion[] {
  const other = input.kind === 'transit'
    ? filterHdJustNowPersonalityActivations(input.otherActivations)
    : filterHdCycleOverlayActivations(input.otherActivations);
  if (other.length === 0) return [];

  const targetCenters = new Set<HdBodygraphCenter>(
    input.center != null
      ? [input.center]
      : deriveCenters(input.renderStateFactory.create(input.ownConfiguration)).undefined.map(entry => entry.center),
  );
  if (targetCenters.size === 0) return [];

  const completing = new Set<string>();
  for (const conditioned of buildHdCycleConditionedCenters(input.ownConfiguration, other, input.renderStateFactory)) {
    for (const cause of conditioned.causes) completing.add(`${conditioned.center}:${cause.planetId}:${cause.gate}`);
  }

  const seen = new Set<string>();
  const suggestions: HdMindGateSuggestion[] = [];
  for (const activation of other) {
    const center = HD_BODYGRAPH_GATE_TO_CENTER[activation.gate];
    if (center == null || !targetCenters.has(center)) continue;
    const key = `${activation.planetId}:${activation.gate}:${activation.line}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
    if (!config) continue;
    suggestions.push({
      planetKey: config.key,
      planetId: activation.planetId,
      glyph: config.astronomiconChar,
      color: config.stripeColor,
      gate: activation.gate,
      line: activation.line,
      center,
      completesChannel: completing.has(`${center}:${activation.planetId}:${activation.gate}`),
    });
  }
  return suggestions.sort((a, b) =>
    Number(b.completesChannel) - Number(a.completesChannel)
    || (PLANET_ORDER.get(a.planetId) ?? 99) - (PLANET_ORDER.get(b.planetId) ?? 99)
    || a.gate - b.gate
    || a.line - b.line);
}
